/**
 * Multi-option variant picker — colour swatches + text cards, combination
 * availability marking (cards fade, swatches keep their colour and take a
 * strike), hidden variant id + URL sync, notifies product-form.
 */
(() => {
  const TAG = 'product-variant-picker';

  if (customElements.get(TAG)) return;

  const optionKey = (position) => `option${position}`;

  // Swatches and text cards share the option markup but not the modifier
  // classes. A card's `--unavailable` fades it; a swatch keeps its colour and
  // takes a strike, so each button only ever gets its own block's modifiers.
  const blockName = (button) =>
    button.classList.contains('variant-picker__swatch')
      ? 'variant-picker__swatch'
      : 'variant-picker__card';

  class ProductVariantPicker extends HTMLElement {
    #variants = [];
    #threshold = 5;
    #showLowStock = true;
    #selected = { option1: null, option2: null, option3: null };

    connectedCallback() {
      this.#readParentStockSettings();
      this.#variants = this.#readVariants();
      this.#syncSelectedFromDom();
      this.#bindOptions();
      this.#refreshAvailability();
      this.#updateStock(this.#currentVariant());
    }

    disconnectedCallback() {
      for (const button of this.#valueButtons()) {
        button.removeEventListener('click', this.#handleValueClick);
      }
    }

    getVariantById(id) {
      return this.#variants.find((item) => String(item.id) === String(id)) || null;
    }

    getCurrentVariant() {
      return this.#currentVariant();
    }

    #productForm() {
      return this.closest('product-form') || this.closest('[data-product-form]');
    }

    #readParentStockSettings() {
      const form = this.#productForm();

      if (!form) return;

      if (form.dataset.showLowStock != null) {
        this.#showLowStock = form.dataset.showLowStock === 'true';
      }

      if (form.dataset.lowStockThreshold != null) {
        this.#threshold = Number(form.dataset.lowStockThreshold || 5);
      }
    }

    #readVariants() {
      const script = this.querySelector('[data-variants-json]');

      if (!script) return [];

      try {
        return JSON.parse(script.textContent || '[]');
      } catch {
        return [];
      }
    }

    #optionRoots() {
      return this.querySelectorAll('[data-option]');
    }

    #valueButtons(root = this) {
      return root.querySelectorAll('[data-option-value]');
    }

    #form() {
      return this.closest('form');
    }

    #variantIdInput() {
      return this.#form()?.querySelector('input[name="id"]') || null;
    }

    #bindOptions() {
      for (const button of this.#valueButtons()) {
        button.addEventListener('click', this.#handleValueClick);
      }
    }

    #syncSelectedFromDom() {
      for (const option of this.#optionRoots()) {
        const position = Number(option.dataset.optionPosition || 0);
        const selected = option.querySelector(
          '[data-option-value][aria-checked="true"]'
        );
        const value = selected?.getAttribute('data-option-value');

        if (position && value) {
          this.#selected[optionKey(position)] = value;
        }
      }

      const input = this.#variantIdInput();
      const fromInput = this.getVariantById(input?.value);

      if (fromInput) {
        this.#selected.option1 = fromInput.option1 || this.#selected.option1;
        this.#selected.option2 = fromInput.option2 || this.#selected.option2;
        this.#selected.option3 = fromInput.option3 || this.#selected.option3;
      }
    }

    #handleValueClick = (event) => {
      const button = event.currentTarget;

      if (!(button instanceof HTMLElement)) return;

      const option = button.closest('[data-option]');
      const value = button.getAttribute('data-option-value');
      const position = Number(option?.dataset.optionPosition || 0);

      if (!option || !value || !position) return;

      this.#selected[optionKey(position)] = value;
      this.#selectValue(option, button, value);

      const variant = this.#resolveVariantForOptionChange(position, value);

      if (!variant) return;

      this.#applyVariant(variant);
      this.#refreshAvailability();
    };

    #selectValue(option, activeButton, value) {
      for (const button of this.#valueButtons(option)) {
        const selected = button === activeButton;

        button.classList.toggle(`${blockName(button)}--selected`, selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      }

      const label = option.querySelector('[data-selected-value]');

      if (label) {
        label.textContent = value;
      }
    }

    #resolveVariant({ preferAvailable = true } = {}) {
      const matches = this.#variants.filter((variant) => {
        for (let position = 1; position <= 3; position += 1) {
          const key = optionKey(position);
          const selected = this.#selected[key];

          if (!selected) continue;

          if (variant[key] !== selected) return false;
        }

        return true;
      });

      if (matches.length === 0) return null;

      if (preferAvailable) {
        return matches.find((item) => item.available !== false) || null;
      }

      return matches[0];
    }

    /**
     * After an option click: keep sibling selections when possible, otherwise
     * jump to another available combination that includes the clicked value.
     */
    #resolveVariantForOptionChange(position, value) {
      const key = optionKey(position);

      const withCurrentOthers = this.#variants.filter((variant) => {
        if (variant[key] !== value) return false;

        for (let other = 1; other <= 3; other += 1) {
          if (other === position) continue;

          const selected = this.#selected[optionKey(other)];

          if (!selected) continue;

          if (variant[optionKey(other)] !== selected) return false;
        }

        return true;
      });

      const availableWithCurrent = withCurrentOthers.find(
        (item) => item.available !== false
      );

      if (availableWithCurrent) return availableWithCurrent;

      const anyAvailableWithValue = this.#variants.find(
        (item) => item[key] === value && item.available !== false
      );

      if (anyAvailableWithValue) return anyAvailableWithValue;

      if (withCurrentOthers[0]) return withCurrentOthers[0];

      return this.#variants.find((item) => item[key] === value) || null;
    }

    #currentVariant() {
      const inputId = this.#variantIdInput()?.value;
      const fromInput = this.getVariantById(inputId);

      if (fromInput) return fromInput;

      return this.#resolveVariant({ preferAvailable: false });
    }

    #isValueAvailable(position, value) {
      return this.#variants.some((variant) => {
        if (variant[optionKey(position)] !== value) return false;
        if (variant.available === false) return false;

        for (let other = 1; other <= 3; other += 1) {
          if (other === position) continue;

          const selected = this.#selected[optionKey(other)];

          if (!selected) continue;

          if (variant[optionKey(other)] !== selected) return false;
        }

        return true;
      });
    }

    #refreshAvailability() {
      for (const option of this.#optionRoots()) {
        const position = Number(option.dataset.optionPosition || 0);

        if (!position) continue;

        for (const button of this.#valueButtons(option)) {
          const value = button.getAttribute('data-option-value');

          if (!value) continue;

          const available = this.#isValueAvailable(position, value);

          // Keep options clickable so shoppers can reach sold-out combos;
          // only the unavailable visual treatment is applied.
          button.disabled = false;
          button.classList.toggle(
            `${blockName(button)}--unavailable`,
            !available
          );
        }
      }
    }

    #applyVariant(variant) {
      const input = this.#variantIdInput();

      if (input) {
        input.value = String(variant.id);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.#selected.option1 = variant.option1 || null;
      this.#selected.option2 = variant.option2 || null;
      this.#selected.option3 = variant.option3 || null;

      this.#syncLabelsFromVariant(variant);
      this.#updateStock(variant);
      this.#updateUrl(variant.id);

      this.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant },
        })
      );
    }

    #syncLabelsFromVariant(variant) {
      for (const option of this.#optionRoots()) {
        const position = Number(option.dataset.optionPosition || 0);
        const value = variant[optionKey(position)];

        if (!value) continue;

        const button = Array.from(this.#valueButtons(option)).find(
          (item) => item.getAttribute('data-option-value') === value
        );

        if (button instanceof HTMLElement) {
          this.#selectValue(option, button, value);
        }
      }
    }

    #isOutOfStock(variant) {
      if (!variant) return false;

      const tracked = Boolean(variant.inventory_management);
      const continueSelling = variant.inventory_policy === 'continue';

      return (
        tracked && !continueSelling && variant.available === false
      );
    }

    #stockLabel(variant, stock) {
      if (this.#isOutOfStock(variant)) {
        const restock = variant.restock_date;
        const template = stock.dataset.oosRestockTemplate || '';

        if (restock && template) {
          return template.replace('__date__', restock);
        }

        return stock.dataset.oosLabel || '';
      }

      return stock.dataset.lowStockLabel || '';
    }

    #updateStock(variant) {
      const stock = this.querySelector('[data-stock]');

      if (!stock) return;

      const labelEl = stock.querySelector('.product-stock__label');
      const oos = this.#isOutOfStock(variant);

      if (oos) {
        stock.hidden = false;
        stock.classList.add('product-stock--oos');
        if (labelEl) labelEl.textContent = this.#stockLabel(variant, stock);
        return;
      }

      stock.classList.remove('product-stock--oos');

      if (!this.#showLowStock || !variant) {
        stock.hidden = true;
        return;
      }

      const tracked = Boolean(variant.inventory_management);
      const quantity = Number(variant.inventory_quantity || 0);
      const showLow =
        tracked &&
        variant.available !== false &&
        quantity > 0 &&
        quantity <= this.#threshold;

      stock.hidden = !showLow;

      if (showLow && labelEl) {
        labelEl.textContent = this.#stockLabel(variant, stock);
      }
    }

    #updateUrl(variantId) {
      if (this.hasAttribute('data-no-url-sync')) return;
      if (!variantId || !window.history?.replaceState) return;

      try {
        const url = new URL(window.location.href);

        url.searchParams.set('variant', String(variantId));
        window.history.replaceState({}, '', url.toString());
      } catch {
        // Ignore URL failures (file://, etc.)
      }
    }
  }

  customElements.define(TAG, ProductVariantPicker);
})();
